import { connect } from 'pwa-helpers';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import '../product-card/product-card-display';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { BaseComponent } from '../base-component/base-component';
import { PLACEMENT_TOP } from '../../common/constants/bs';
import emit from '../../common/events';
import "../pagination/message/pagination-message";
import { selectTotalForPagination, selectV2Request } from './store/search-v2.selectors';
import { performV2Search } from './thunks/perform-search-v2.thunk';
import { SearchV2Request } from './interface/search-v2.interface';
import "./search-v2-pagination-message";

export declare interface PaginationPage {
  label: string;
  index: number;
}

/**
 * An element to paginate search results
 *
 * @csspart page-button - Each page button of the pagination.
 * @csspart current-page-button - The current page button.
 * @csspart previous - The previous button arrow.
 * @csspart next - The next button arrow.
 *
 * @event go-to-page - A user has clicked a page within the pagination.
 *        <br><br><span style="color: green">&check;</span> dataLayer
 *        <br><span style="color: green">&check;</span> bubbles
 *        <br><br>event.detail: { searchTerms, catalog, pageLength, start, facets }
 */
@customElement('mhecomm-search-v2-pagination')
export class Pagination extends connect(store)(BaseComponent) {
  /**
   * Overriding base components unloadedHeight
   */
  unloadedHeight = '58px';

  /**
   * The number of pages to show in the pagination. This does not include the first and last pages or the arrows. It should be an odd number.
   */
  @property({ type: Number })
  maxPagesShown = 3;

  /**
   * The message to display to the user when there are results. The following placeholders are available: %first% %last% %total% %term%
   */
  @property()
  message = 'Showing %first%-%last% of %total% Results Matching "%term%"';

  @state()
  private currentPage = 0;

  @state()
  private total = 0;

  @state()
  private showPrevious = false;

  @state()
  private showNext = false;

  @state()
  private pages: Array<PaginationPage> = [];

  @state()
  private firstPage?: PaginationPage;

  @state()
  private finalPage?: PaginationPage;

  @state()
  private pageLength = 10;

  @state()
  private request?: SearchV2Request;

  renderComp() {
    return this.paginationComponent();
  }

  paginationComponent() {
    return this.total > this.pageLength
      ? html`
          <nav
            role="navigation"
            class="d-flex align-items-center gap-3 flex-column flex-lg-row"
            aria-label="Pagination"
          >
            <span id="paginationPhrase" data-automation-id="paginationPhrase" class="flex-grow-1">
              ${this.showPaginationMsg()}
            </span>
            <ul class="pagination text-center mb-0 gap-1">
              ${this.prevStep()} ${this.firstPageItem()} ${this.pageItems()} ${this.finalPageItem()} ${this.nextStep()}
            </ul>
          </nav>
        `
      : nothing;
  }

  showPaginationMsg() {
    return html`
      <mhecomm-search-v2-pagination-message
        message="${this.message}"
        id="pagination_message"
        data-automation-id="pagination_message"
      >
      </mhecomm-search-v2-pagination-message>
    `;
  }

  prevStep() {
    return html`
      <li class="page-item ${this.showPrevious ? '' : 'disabled'}">
        <a
          tabindex="${this.showPrevious ? '0' : '-1'}"
          data-bs-toggle="tooltip"
          data-bs-placement=${PLACEMENT_TOP}
          title="Previous Page"
          class="page-link text-dark border-0"
          aria-disabled="${!this.showPrevious}"
          aria-label="Previous Page"
          href="javascript:void(0)"
          id="page-previous"
          data-automation-id="page-previous"
          @click=${() => (this.showPrevious ? this.goToPage(this.currentPage - 1) : null)}
        >
          <uxt-icon icon="chevron-left" size="md"></uxt-icon>
        </a>
      </li>
    `;
  }

  nextStep() {
    return html`
      <li class="page-item ${this.showNext ? '' : 'disabled'}">
        <a
          tabindex="${this.showNext ? '0' : '-1'}"
          data-bs-toggle="tooltip"
          data-bs-html="true"
          title="Next Page"
          class="page-link text-dark border-0"
          data-bs-placement=${PLACEMENT_TOP}
          aria-disabled="${!this.showNext}"
          aria-label="Next Page"
          href="javascript:void(0)"
          id="page-next"
          data-automation-id="page-next"
          @click=${() => (this.showNext ? this.goToPage(this.currentPage + 1) : null)}
        >
          <uxt-icon icon="chevron-right" size="md"></uxt-icon>
        </a>
      </li>
    `;
  }

  firstPageItem() {
    return html`
      ${this.firstPage
        ? html`
            <li class="page-item">
              <a
                class="page-link text-dark border-0"
                href="javascript:void(0)"
                id="page-${this.firstPage.index + 1}"
                data-automation-id="page-${this.firstPage.index + 1}"
                @click=${() => this.goToPage(this.firstPage ? this.firstPage.index : 0)}
                aria-label="Page ${this.firstPage.label}"
              >
                ${this.firstPage.label}
              </a>
            </li>
            <li class="page-item align-self-center disabled">
              <span class="visually-hidden">ellipsis</span>
              <span class="ellipsis" aria-hidden="true">…</span>
            </li>
          `
        : ''}
    `;
  }

  finalPageItem() {
    return html`
      ${this.finalPage
        ? html`
            <li class="page-item align-self-center disabled">
              <span class="visually-hidden">ellipsis</span>
              <span class="ellipsis" aria-hidden="true">…</span>
            </li>
            <li class="page-item">
              <a
                class="page-link text-dark border-0"
                href="javascript:void(0)"
                id="page-${this.finalPage.index + 1}"
                data-automation-id="page-${this.finalPage.index + 1}"
                @click=${() => this.goToPage(this.finalPage ? this.finalPage.index : 0)}
                aria-label="Page ${this.finalPage.label}"
              >
                ${this.finalPage.label}
              </a>
            </li>
          `
        : ''}
    `;
  }

  pageItems() {
    return html`
      ${this.pages.map(
        (page) => html`
          <li class="page-item ${this.currentPage === page.index ? 'active' : ''}">
            <a
              class="page-link text-dark border-0"
              href="javascript:void(0)"
              .disabled=${this.currentPage === page.index}
              id="page-${page.index + 1}"
              data-automation-id="page-${page.index + 1}"
              @click=${() => (this.currentPage !== page.index ? this.goToPage(page.index) : '')}
              aria-label="Page ${page.label}"
              aria-current=${ifDefined(this.currentPage === page.index ? 'page' : undefined)}
            >
              ${page.label}
            </a>
          </li>
        `
      )}
    `;
  }

  goToPage(index: number) {
    if (this.pageLength) {
      store.dispatch(
        performV2Search({
          start: index * this.pageLength + 1,
          pageLength: this.request?.pageLength || 1,
          searchTerms: this.request?.searchTerms,
          fieldValueFilter: this.request?.fieldValueFilter
        })
      ).then(() => this._focusSearchResultsRegion());

      emit({
        element: this,
        name: 'go-to-page',
        detail: { 
          paginationNumber: index + 1,
          catalog: 'SUPPORT',
         },
        dataLayer: true,
      });
    }
  }

  /**
   * Sets focus to the search results info bar for screen reader announcement.
   * Only applies when the aria-live region is not being used on the pagination message component.
   */
  private _focusSearchResultsRegion(): void {
    const searchResultsComponent = document.querySelector('mhecomm-search-v2-pagination-message');
    const useAriaLive = searchResultsComponent?.getAttribute('use-aria-live') === 'true';

    if (searchResultsComponent?.shadowRoot && !useAriaLive) {
      const resultsInfoBar = searchResultsComponent.shadowRoot.getElementById('results-info-bar');
      resultsInfoBar?.focus();
    }
  }

  stateChanged(state: StoreState) {
    this.request = selectV2Request(state);
    const total = selectTotalForPagination(state) || 0;
    let currentPage = 0;

    if (this.request && this.request?.start >= this.request?.pageLength) {
      currentPage = Math.floor((this.request?.start - 1) / this.request?.pageLength);
    }

    if (this.request && (this.total != total || this.currentPage != currentPage)) {
      this.total = total;
      this.currentPage = currentPage;
      this.pageLength = this.request?.pageLength | 10;
      this.updatePages();
    }
  }

  private updatePages() {
    const totalPageCount = Math.ceil(this.total / this.pageLength);

    this.showPrevious = this.currentPage > 0;
    this.firstPage = this.determineFirstPage();
    this.pages = this.mainItems(totalPageCount);
    this.finalPage = this.determineFinalPage(
      this.pages && this.pages.length > 0 ? this.pages[this.pages.length - 1].index : 0,
      totalPageCount
    );
    this.showNext = this.currentPage + 1 < totalPageCount;
  }

  private determineFirstPage() {
    if (this.currentPage - Math.floor(this.maxPagesShown / 2) > 1) {
      return {
        label: '1',
        index: 0,
      };
    } else {
      return undefined;
    }
  }

  private mainItems(totalPageCount: number) {
    const pages: Array<PaginationPage> = [];
    const halfMaxPagesShown = Math.floor(this.maxPagesShown / 2);
    let start = this.currentPage - halfMaxPagesShown;
    let end = this.currentPage + halfMaxPagesShown + 1;

    if (this.maxPagesShown >= totalPageCount) {
      start = 0;
      end = totalPageCount - 1;
    } else if (this.currentPage + halfMaxPagesShown >= totalPageCount - 2) {
      end = totalPageCount - 1;
    } else if (this.currentPage + halfMaxPagesShown >= totalPageCount - 1) {
      start = totalPageCount - this.maxPagesShown;
      end = totalPageCount - 1;
    } else if (this.currentPage - halfMaxPagesShown == 1 && this.maxPagesShown <= totalPageCount) {
      start = 0;
    } else if (this.currentPage - halfMaxPagesShown <= 0 && this.maxPagesShown <= totalPageCount) {
      start = 0;
      end = this.maxPagesShown;
    }

    if (end - start >= 1) {
      for (let i = start; i <= end; i += 1) {
        pages.push({
          label: (i + 1).toString(),
          index: i,
        });
      }
    }

    return pages;
  }

  private determineFinalPage(lastShown: number, totalPageCount: number) {
    if (lastShown + 1 < totalPageCount) {
      return {
        label: totalPageCount.toString(),
        index: totalPageCount - 1,
      };
    } else {
      return undefined;
    }
  }

  updated() {
    this.bsInit('[data-bs-toggle="tooltip"]', window.bootstrap?.Tooltip);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search-v2-pagination': Pagination;
  }
}